// File: frontend/src/components/Admin/LineSettingsModal.jsx

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { lineAPI } from '../../services/api';
import Modal from '../Modal';

export default function LineSettingsModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [schoolName, setSchoolName] = useState('Irie English Academy (愛瑞美語)');
  const [channelAccessToken, setChannelAccessToken] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [maskedToken, setMaskedToken] = useState('');
  const [hasToken, setHasToken] = useState(false);

  // Toggles
  const [notifyCheckin, setNotifyCheckin] = useState(true);
  const [notifyLeave, setNotifyLeave] = useState(true);
  const [notifyBilling, setNotifyBilling] = useState(true);

  // Test Push
  const [testLineId, setTestLineId] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await lineAPI.getSettings();
      setSchoolName(data.school_name || 'Irie English Academy (愛瑞美語)');
      setMaskedToken(data.line_channel_access_token_masked || '');
      setHasToken(data.has_token || false);
      setNotifyCheckin(data.line_auto_notify_checkin);
      setNotifyLeave(data.line_auto_notify_leave);
      setNotifyBilling(data.line_auto_notify_billing);
    } catch (err) {
      console.error('Failed to load LINE settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await lineAPI.saveSettings({
        school_name: schoolName,
        line_channel_access_token: channelAccessToken || undefined,
        line_channel_secret: channelSecret || undefined,
        line_auto_notify_checkin: notifyCheckin,
        line_auto_notify_leave: notifyLeave,
        line_auto_notify_billing: notifyBilling,
      });

      toast.success('LINE official settings saved!');
      setChannelAccessToken('');
      setChannelSecret('');
      fetchSettings();
      onClose();
    } catch (err) {
      toast.error('Failed to save LINE settings.');
    }
  };

  const handleSendTest = async () => {
    if (!testLineId.trim()) {
      toast.error('Please enter a LINE User ID to test.');
      return;
    }

    setSendingTest(true);
    try {
      await lineAPI.sendTest(testLineId.trim());
      toast.success('Test message delivered to LINE!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send test message.');
    } finally {
      setSendingTest(false);
    }
  };

  const webhookUrl = `${window.location.origin.replace(/\/$/, '')}/api/line/webhook`;

  return (
    <Modal title="LINE Official Account Settings (LINE 官方推播設定)" isOpen={isOpen} onClose={onClose}>
      {loading ? (
        <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">Loading settings...</div>
      ) : (
        <form onSubmit={handleSave} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">School Display Name (補習班/教室名稱)</label>
            <input
              type="text"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              className="w-full border rounded p-2 text-xs"
              required
            />
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs">
            <div className="font-bold text-emerald-900 mb-1">🔗 LINE Webhook URL</div>
            <div className="flex items-center gap-2">
              <code className="bg-white dark:bg-gray-800 border rounded px-2 py-1 text-[11px] font-mono flex-1 text-gray-800 dark:text-gray-200">
                {webhookUrl}
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(webhookUrl);
                  toast.success('Webhook URL copied!');
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-[11px] text-emerald-700 mt-1">
              Paste this into your <strong>LINE Developers Console → Messaging API → Webhook URL</strong>.
            </p>
          </div>

          <div>
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Channel Access Token (長期存取權杖)
              {hasToken && <span className="ml-2 text-emerald-600 font-bold">✓ Configured ({maskedToken})</span>}
            </label>
            <textarea
              value={channelAccessToken}
              onChange={(e) => setChannelAccessToken(e.target.value)}
              placeholder={hasToken ? "Leave blank to keep existing token, or paste a new token here" : "Paste your Channel Access Token from LINE Developers"}
              className="w-full border rounded p-2 font-mono text-[11px]"
              rows="2"
            ></textarea>
          </div>

          <div>
            <label className="block font-semibold text-gray-700 dark:text-gray-300 mb-1">Channel Secret (頻道密鑰)</label>
            <input
              type="password"
              value={channelSecret}
              onChange={(e) => setChannelSecret(e.target.value)}
              placeholder="Paste your Channel Secret"
              className="w-full border rounded p-2 font-mono text-xs"
            />
          </div>

          {/* Feature Toggles */}
          <div className="border-t pt-3 space-y-2">
            <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">Auto-Notification Toggles (自動推播開關)</div>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyCheckin}
                onChange={(e) => setNotifyCheckin(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="text-gray-700 dark:text-gray-300">課堂簽到推播 (Auto-notify parents on student arrival)</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyLeave}
                onChange={(e) => setNotifyLeave(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="text-gray-700 dark:text-gray-300">請假與補課審核推播 (Auto-notify parents when leave is approved/arranged)</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyBilling}
                onChange={(e) => setNotifyBilling(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="text-gray-700 dark:text-gray-300">學費繳款提醒推播 (Auto-notify parents with Land Bank transfer info)</span>
            </label>
          </div>

          {/* Test Push Section */}
          <div className="border-t pt-3">
            <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">Send Test Push (測試推播)</div>
            <div className="flex gap-2">
              <input
                type="text"
                value={testLineId}
                onChange={(e) => setTestLineId(e.target.value)}
                placeholder="Enter LINE User ID (e.g. U123456789...)"
                className="flex-1 border rounded p-2 font-mono text-xs"
              />
              <button
                type="button"
                disabled={sendingTest || !hasToken}
                onClick={handleSendTest}
                className="bg-gray-800 hover:bg-black text-white px-3 py-1.5 rounded font-medium disabled:opacity-50"
              >
                {sendingTest ? 'Sending...' : 'Send Test'}
              </button>
            </div>
            {!hasToken && (
              <p className="text-[10px] text-amber-600 mt-1">
                Save your Channel Access Token first to enable sending test messages.
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 text-gray-800 dark:text-gray-200 px-4 py-2 rounded font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded font-bold"
            >
              Save Settings
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
